import React, { useState, useRef } from 'react';
import {
    Image,
    ScrollView,
    Text,
    TouchableOpacity,
    View,
    Keyboard
} from 'react-native-web';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import 'react-image-picker-editor/dist/index.css'

// Custom Components
import { Button, TextBox, CuisinePickerModal, CityPickerModal } from '../../components';

// SVGs
import { ReactComponent as PlaceHolderLogo } from '../../images/svg_placeholder_logo.svg';

// Utils
import { checkValidURL, showAlert } from '../../utils/util_helpers';

// Globals
import GLOBAL from '../../configs/config_global';

// Consts
const LOGO_SIZE = 140;

function TabTruckInfo(props) {
    const {
        truckCuisineList,
        onCuisineSelected,
        selectedCities,
        onCitiesSelected,
        onLicenseNeededCitiesUpdated,
        licenseNeededCities,
        onNextButtonPressed,
        onTruckWebsiteChanged,
        truckWebsite,
        onLogoChanged,
        onCoverChanged,
        saveTempDataFunc,
        isLoading,
        logoPhoto,
        coverPhoto,
        onSwitchToSearchPressed
    } = props;

    // region Local State
    const [logoPhotoUri, setLogoPhotoUri] = useState(null);
    const [coverPhotoUri, setCoverPhotoUri] = useState(null);
    const [brandPhotoPosition, setBrandPhotoPosition] = useState(0);

    const logoPickerRef = useRef(null);
    const coverPickerRef = useRef(null);
    const cuisineSelectorRef = useRef(null);
    const citySelectorRef = useRef(null);
    // endregion

    // region Render Title and Description
    const renderTitleAndDescription = () => (
        <View style={{ width: '100%' }}>
            <Text style={{ fontSize: 18, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 16, lineHeight: 24, textAlign: 'center', width: '100%' }}>
                Tell your customers about your awesome food truck!
            </Text>

            <TouchableOpacity onPress={onSwitchToSearchPressed} style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'center', marginTop: 16 }}>
                <FeatherIcon name={'search'} size={20} color={GLOBAL.COLOR.BASIC.UI_BLUE} style={{ marginRight: 8 }} />

                <Text style={{ fontSize: 16, color: GLOBAL.COLOR.BASIC.UI_BLUE, lineHeight: 24, textAlign: 'center', width: '100%', fontWeight: 'bold' }}>
                    Search for existing food truck profiles instead
                </Text>
            </TouchableOpacity>
        </View>
    );
    // endregion

    // region Render Cuisine Picker
    const renderCuisinePicker = () => (
        <View style={{ alignSelf: 'stretch' }}>
            <Text style={{ color: 'rgb(117, 117, 117)', letterSpacing: 0.5, fontWeight: 'bold', fontSize: 14, marginTop: 24 }}>
                SERVING CUISINE <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            {/* Cuisine Input */}
            <TextBox
                title={'Serving Cuisines'}
                placeholder={'Tap to select cuisines'}
                onPress={() => cuisineSelectorRef.current.showModal()}
                value={truckCuisineList.length > 0 ? truckCuisineList.map((item) => item.name).join(', ') : ''}
            />
        </View>
    );
    // endregion

    // region Render Serving City Picker
    const renderServingCitiesPicker = () => (
        <View style={{ alignSelf: 'stretch' }}>
            <Text style={{ color: 'rgb(117, 117, 117)', letterSpacing: 0.5, fontWeight: 'bold', fontSize: 14, marginTop: 32 }}>
                SERVING CITIES <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            {/* City Input */}
            <TextBox
                title={'Serving Cities'}
                placeholder={'Tap to select cities'}
                onPress={() => citySelectorRef.current.showModal()}
                value={selectedCities.length > 0 ? selectedCities.map((id) => GLOBAL.CITIES[id].name).join(', ') : ''}
            />
        </View>
    );
    // endregion

    // region Render Required License Number Cities
    const renderRequiredLicenseNumberCities = () => (
        !!licenseNeededCities?.length &&
        <View style={{ alignSelf: 'stretch', marginTop: 12 }}>
            <Text style={{ color: GLOBAL.COLOR.BASIC.UI_BLUE, letterSpacing: 0.5, fontWeight: 'bold', fontSize: 12, marginTop: 32 }}>
                LICENSE NUMBERS <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            <Text style={{ marginTop: 8, fontSize: 12, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 18 }}>
                You have selected one or more cities that require declaration of mobile food license number(s), please enter them below
            </Text>

            {/* Input */}
            {
                licenseNeededCities.map((city, index) => (
                    <View
                        key={city.id}
                        style={{ alignSelf: 'stretch', height: 48, flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                        <TextBox
                            title={`${city.name}, ${city.state.short_name}`}
                            onTitlePressed={() => showAlert({ text: `${city.name} (${city.state.short_name}) mobile food license number is required` })}
                            placeholder={'License Number'}
                            returnKeyType={'done'}
                            autoCorrect={false}
                            style={{ marginTop: 0, flex: 1 }}
                            textInputStyle={{ fontWeight: '500' }}
                            onChangeText={(text) => {
                                const tempArr = cloneDeep(licenseNeededCities);
                                tempArr.splice(index, 1, { ...licenseNeededCities[index], value: text });
                                onLicenseNeededCitiesUpdated(tempArr);
                            }}
                            value={licenseNeededCities[index]?.value}
                            onSubmitEditing={() => Keyboard.dismiss()}
                            onBlur={() => {
                                onLicenseNeededCitiesUpdated(licenseNeededCities);
                                setTimeout(() => saveTempDataFunc(), 500);
                            }}
                        />
                    </View>
                ))
            }
        </View>
    );
    // endregion

    // region Render Website Input
    const renderWebsiteInput = () => (
        <View style={{ alignSelf: 'stretch', marginTop: 16 }}>
            <Text style={{ color: 'rgb(117, 117, 117)', letterSpacing: 0.5, fontWeight: 'bold', fontSize: 14, marginTop: 24 }}>
                WEBSITE <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            <Text style={{ marginTop: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 18 }}>
                If you don't have a website, please enter in your Facebook page or other relevant link here
            </Text>

            <TextBox
                title={'Website'}
                placeholder={'gotruckster.com'}
                keyboardType={'email-address'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                autoCorrect={false}
                containerStyle={{ flex: 1, marginTop: 8 }}
                onSubmitEditing={() => Keyboard.dismiss()}
                onChangeText={onTruckWebsiteChanged}
                value={truckWebsite}
                showError={!!truckWebsite && !checkValidURL(truckWebsite)}
                onBlur={() => setTimeout(() => saveTempDataFunc(), 500)}
            />
        </View>
    );
    // endregion

    // region Render Basic Information
    const renderBasicInfoView = () => (
        <View style={{ backgroundColor: 'white', width: '100%', padding: 24 }}>
            <Text style={{ fontWeight: '900', fontSize: 20, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                Basic Information
            </Text>

            {/* Cuisine Picker */}
            {renderCuisinePicker()}

            {/* Serving Cities Picker */}
            {renderServingCitiesPicker()}

            {/* Required License Number Cities */}
            {renderRequiredLicenseNumberCities()}

            {/* Website Picker */}
            {renderWebsiteInput()}
        </View>
    );
    // endregion

    // region Render Photo Picker View
    const renderBrandPhotoView = () => (
        <View
            onLayout={(event) => !brandPhotoPosition && setBrandPhotoPosition(event.nativeEvent.layout.y)}
            style={{ backgroundColor: 'white', width: '100%', padding: 24, marginTop: 16 }}>
            <Text style={{ fontWeight: '900', fontSize: 20, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                Brand Photos
            </Text>

            {/* Logo */}
            <Text style={{ color: 'rgb(117, 117, 117)', letterSpacing: 0.5, fontWeight: 'bold', fontSize: 14, marginTop: 24 }}>
                LOGO PHOTO <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            <Text style={{ marginTop: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 18 }}>
                Your logo helps users easily recognize your brand. You can always edit this later.
            </Text>

            <View style={{ alignSelf: 'stretch', paddingTop: 24, alignItems: 'center' }}>
                {/* Logo Picker Button */}
                <TouchableOpacity
                    onPress={() => logoPickerRef.current?.click()}
                    style={{ width: LOGO_SIZE, height: LOGO_SIZE, borderRadius: LOGO_SIZE / 2, borderWidth: 2, borderColor: GLOBAL.COLOR.BASIC.GRAY_OPAQUE, overflow: 'hidden' }}>
                    {
                        logoPhotoUri ?
                            <Image
                                source={{ uri: logoPhotoUri }}
                                style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                                resizeMode={'cover'}
                            />
                            :
                            <PlaceHolderLogo width={LOGO_SIZE} height={LOGO_SIZE} />
                    }
                </TouchableOpacity>

                {/* Picker */}
                <input
                    id={'logoPicker'}
                    type={'file'}
                    accept={'.png, .jpg, .jpeg'}
                    ref={logoPickerRef}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        const file = event.target.files[0];
                        if (file) {
                            onLogoChanged(file);
                            setLogoPhotoUri(URL.createObjectURL(file));
                        }
                    }}
                />
            </View>

            {/* Cover Photo */}
            <Text style={{ color: 'rgb(117, 117, 117)', letterSpacing: 0.5, fontWeight: 'bold', fontSize: 14, marginTop: 40 }}>
                COVER PHOTO <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
            </Text>

            <Text style={{ marginTop: 8, fontSize: 14, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, lineHeight: 18, marginBottom: 24 }}>
                Upload the best angle of your food truck here. You can always edit this later.
            </Text>

            {/* Cover Photo View */}
            <TouchableOpacity
                onPress={() => coverPickerRef.current?.click()}
                style={{ width: '100%', height: LOGO_SIZE * 2, borderRadius: 12, borderWidth: 2, borderColor: GLOBAL.COLOR.BASIC.GRAY_OPAQUE, overflow: 'hidden' }}>
                <Image
                    source={coverPhotoUri ? { uri: coverPhotoUri } : require('../../images/image_placeholder_cover.png')}
                    style={{ width: '100%', height: LOGO_SIZE * 2 }}
                    resizeMode={'cover'}
                />
            </TouchableOpacity>

            {/* Picker */}
            <input
                id={'coverPicker'}
                type={'file'}
                accept={'.png, .jpg, .jpeg'}
                ref={coverPickerRef}
                style={{ display: 'none' }}
                onChange={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    const file = event.target.files[0];
                    if (file) {
                        onCoverChanged(file);
                        setCoverPhotoUri(URL.createObjectURL(file));
                    }
                }}
            />
        </View>
    );
    // endregion

    // region MAIN RENDER
    return(
        <View key={'truckInfo'} style={{ flex: 1, width: '100%', alignItems: 'center' }}>
            {/* Title and description */}
            {renderTitleAndDescription()}

            {/* Text Boxes */}
            <View style={{ flex: 1, width: '50%', marginTop: 24 }}>
                {/* Content */}
                <ScrollView
                    style={{ flex: 1, width: '100%', backgroundColor: 'white' }}
                    contentContainerStyle={{ paddingBottom: 150 }}
                    showsVerticalScrollIndicator={false}>
                    {/* Basic Information */}
                    {renderBasicInfoView()}

                    {/* Photos */}
                    {renderBrandPhotoView()}
                </ScrollView>

                {/* Gradient */}
                <Image
                    source={require('../../images/image_gradient_down.png')}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 20 }}
                />
            </View>

            {/* Continue Button */}
            <View
                animation={'fadeInUp'}
                duration={600}
                cardElevation={3}
                style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, backgroundColor: 'white',
                    paddingTop: 16, paddingHorizontal: 40, paddingBottom: 64
                }}>
                <Button
                    onPress={onNextButtonPressed}
                    style={{ width: '50%', alignSelf: 'center' }}
                    backgroundColor={GLOBAL.COLOR.MAIN}
                    text={'Continue'}
                    textColor={'white'}
                    isLoading={isLoading}
                    locked={!truckCuisineList.length || !selectedCities.length || !truckWebsite || !logoPhoto || !coverPhoto}
                />
            </View>

            {/* Cuisine Picker Modal */}
            <CuisinePickerModal
                ref={cuisineSelectorRef}
                onSelectComplete={(cuisines) => {
                    onCuisineSelected(cuisines);
                    setTimeout(() => saveTempDataFunc(), 500);
                }}
                selectedCuisines={truckCuisineList}
            />

            {/* City Selector Popover */}
            <CityPickerModal
                ref={citySelectorRef}
                onCitySelected={(selectedIDs) => {
                    onCitiesSelected(selectedIDs);
                    onLicenseNeededCitiesUpdated(
                        selectedIDs
                            .filter((id) => GLOBAL.CITIES[id].requireLicenseNumber)
                            .map((id) => ({ ...cloneDeep(GLOBAL.CITIES[id]), value: '' }))
                    );
                    setTimeout(() => saveTempDataFunc(), 500);
                }}
                selectedCityIDs={selectedCities}
            />
        </View>
    );
    // endregion
}

// region Props
TabTruckInfo.propTypes = {
    truckName: PropTypes.string,
    onTruckNameChanged: PropTypes.func,
    truckCuisineList: PropTypes.array,
    onCuisineSelected: PropTypes.func,
    selectedCities: PropTypes.array,
    onCitiesSelected: PropTypes.func,
    isLoading: PropTypes.bool,
    truckWebsite: PropTypes.string,
    onTruckWebsiteChanged: PropTypes.func,
    onLogoChanged: PropTypes.func,
    onCoverChanged: PropTypes.func,
    onNextButtonPressed: PropTypes.func,
    saveTempDataFunc: PropTypes.func,
    onLicenseNeededCitiesUpdated: PropTypes.func,
    licenseNeededCities: PropTypes.array,
    logoPhoto: PropTypes.string,
    coverPhoto: PropTypes.string,
    onSwitchToSearchPressed: PropTypes.func
};

TabTruckInfo.defaultProps = {
    truckName: '',
    onTruckNameChanged: () => {},
    truckCuisineList: [],
    onCuisineSelected: () => {},
    selectedCities: [],
    onCitiesSelected: () => {},
    isLoading: false,
    truckWebsite: '',
    onTruckWebsiteChanged: () => {},
    onLogoChanged: () => {},
    onCoverChanged: () => {},
    onNextButtonPressed: () => {},
    saveTempDataFunc: () => {},
    onLicenseNeededCitiesUpdated: () => {},
    licenseNeededCities: [],
    logoPhoto: '',
    coverPhoto: '',
    onSwitchToSearchPressed: () => {}
};
// endregion

export default TabTruckInfo;
